import React, { useMemo, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { fetcher } from '@dashboard/services/request';
import { couponKey } from '@dashboard/lib/queryKeys';
import PageMultiSelect from '@dashboard/components/Formik/components/PageMultiSelect';
import { format as formatDate } from 'date-fns';
import FormError from '@/dashboard/components/Formik/components/FormError';
import * as Yup from 'yup';

const CouponEditDialog = ({ open, onClose, coupon }) => {
  const { sellerId, token } = useAuth();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();

  const updateCoupon = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${sellerId}/coupons/${coupon._id}`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKey(coupon._id) });
      emitToast({
        title: 'Coupon updated',
        status: 'success',
      });
      onClose();
    },
    onError: error => {
      emitToast({
        title: 'Failed to update coupon',
        description: error?.message || 'Please try again or contact support.',
        status: 'error',
      });
    },
  });

  const parseRedeemBy = isoString => {
    if (!isoString) {
      return { date: '', time: '' };
    }
    try {
      const date = new Date(isoString);
      return {
        date: formatDate(date, 'yyyy-MM-dd'),
        time: formatDate(date, 'HH:mm'),
      };
    } catch {
      return { date: '', time: '' };
    }
  };

  const redeemByParts = parseRedeemBy(coupon.redeemBy);

  const pageMultiSelectInitialValue = useMemo(
    () =>
      coupon.pages
        ? coupon.pages.map(page => ({
            pageId: page._id,
          }))
        : [],
    [coupon.pages],
  );

  const pageTicketSelectionsRef = useRef({
    pages: coupon.pages || [],
  });

  const handlePageMultiSelectChange = useCallback(({ pages }) => {
    const uniquePageIds = Array.from(pages.keys());
    pageTicketSelectionsRef.current = {
      pages: uniquePageIds,
    };
  }, []);

  return (
    <Dialog
      title="Edit coupon"
      cancelButton={null}
      confirmButton={null}
      size="xl"
      open={open}
      onCancel={onClose}
    >
      {() => (
        <Form
          validationSchema={Yup.object().shape({
            maxRedemptions: Yup.number().min(0, 'Max redemptions must be at least 0').optional(),
          })}
          showState={false}
          initialValues={{
            label: coupon.label,
            pages: coupon.pages || [],
            maxRedemptions: coupon.maxRedemptions ?? '',
            redeemByDate: redeemByParts.date,
            redeemByTime: redeemByParts.time,
          }}
          onSubmit={async values => {
            try {
              const submitData = {};

              const { pages } = pageTicketSelectionsRef.current;
              if (pages && Array.isArray(pages)) {
                submitData.pageIds = pages;
              }

              if (values.maxRedemptions !== '') {
                submitData.maxRedemptions = Number(values.maxRedemptions);
              }

              if (values.redeemByDate) {
                submitData.redeemBy = new Date(`${values.redeemByDate} ${values.redeemByTime}`);
              }

              submitData.label = values.label;

              await updateCoupon.mutateAsync(submitData);
              return { data: true };
            } catch (error) {
              return { error };
            }
          }}
          fields={({ setFieldValue, values }) => [
            {
              name: 'label',
              label: 'Label',
              required: true,
              type: 'text',
              placeholder: 'Coupon label',
              helpText: 'Internal name for this coupon.',
            },
            {
              type: 'custom',
              renderField: () => (
                <h3 className="text-lg font-bold text-gray-900">Limit to pages</h3>
              ),
            },
            {
              type: 'custom',
              label: 'Pages',
              renderField: () => (
                <>
                  <PageMultiSelect
                    placeholder="Choose pages"
                    includeTicketTypes={false}
                    helpText={'Select the pages this coupon can be used on.'}
                    initialValue={pageMultiSelectInitialValue}
                    onChange={handlePageMultiSelectChange}
                    pageTypes={['checkout', 'event']}
                  />

                  <FormError name="pages" />
                </>
              ),
            },
            {
              type: 'custom',
              renderField: () => (
                <h3 className="text-lg font-bold text-gray-900">Redemption limits</h3>
              ),
            },
            {
              type: 'number',
              placeholder: 'Max times used',
              label: 'Max uses',
              helpText:
                "The total amount of times this coupon can be used. Doesn't limit a single customer from redeeming multiple times.",
              name: 'maxRedemptions',
            },
            {
              label: 'Expires on',
              type: 'columns',
              columns: 2,
              gap: 'sm',
              fields: [
                {
                  type: 'date',
                  name: 'redeemByDate',
                  onChange: e => {
                    setFieldValue('redeemByDate', e.target?.value);

                    if (e.target.value && !values.redeemByTime) {
                      setFieldValue('redeemByTime', '00:00');
                    }
                    if (e.target.value === '') {
                      setFieldValue('redeemByTime', '');
                    }
                  },
                },
                {
                  type: 'time',
                  name: 'redeemByTime',
                },
              ],
              helpText: `Date and time the coupon must be used before. Leave blank for coupons that don't expire.`,
            },
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: 'Save',
            appearance: 'primary',
            size: 'full',
          }}
        />
      )}
    </Dialog>
  );
};

export default CouponEditDialog;
